import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { EFieldTypes, ESqlOperators, EWidget, widgetConstants } from 'scw-digital-boards-widgets';
import { IDataForWidgetSpecificConfiguration } from './advanced-filter-inputs/advanced-filter-inputs.interface';
import { AdvancedFilterManager } from './advanced-filter-manager';
import { IAdvancedFilterConfiguration, IFilterIdentity, TAdvancedFilterConfig } from './advanced-filter.interface';

@Injectable()
export class AdvancedFilterService {
  private readonly translate = inject(TranslateService);
  public readonly manager: AdvancedFilterManager;

  public readonly operatorNames: Record<ESqlOperators, string> = Object.values(ESqlOperators).reduce(
    (reducePayload, op) => ({ ...reducePayload, [op]: this.translate.instant(`advanceFilter.operator.${op}`) }),
    {} as Record<ESqlOperators, string>,
  );

  constructor(router: Router) {
    let child = router.routerState.snapshot.root;

    while (child.firstChild) {
      child = child.firstChild;
    }

    const manager = child.data?.['advancedFilterManager'] as AdvancedFilterManager | undefined;

    if (!manager) {
      throw 'Missing AdvancedFilterManager';
    }

    this.manager = manager;
  }

  public extractConfiguration(identity: IFilterIdentity): TAdvancedFilterConfig | undefined {
    if (identity.field === 'date' && identity.operator === ESqlOperators.$period) {
      return {
        dataKey: 'dateRange',
        operators: [ESqlOperators.$period],
        type: EFieldTypes.predefined,
      };
    }

    const configOfMatchingField = this.manager
      .configuration()
      .find(({ field, idOfCustom }) => (idOfCustom ? identity.idOfCustom === idOfCustom : field === identity.field));

    return configOfMatchingField?.configurations.find(({ operators }) =>
      (operators as ESqlOperators[]).includes(identity.operator),
    );
  }

  public getConfigurationsOfWidget<Widget extends EWidget>(
    dataForWidgetSpecificConfiguration: IDataForWidgetSpecificConfiguration<Widget>,
    isEqAllowed: boolean,
  ): IAdvancedFilterConfiguration[] {
    return widgetConstants[dataForWidgetSpecificConfiguration.widgetType]
      .filterModes({
        typeBasedConfiguration: dataForWidgetSpecificConfiguration.typeBasedConfiguration,
      })
      .find(({ name }) => name === dataForWidgetSpecificConfiguration.selectedFilterMode)!
      .filters.map(({ identity }) => {
        const matchingField = this.manager.configuration().find(({ field }) => field === identity.field);

        if (!matchingField) {
          throw "Filters demanded by the widget is not registered to the page's configuration";
        }

        return {
          ...matchingField,
          configurations: matchingField.configurations
            .map((configuration) => {
              const isConfigurationForMultiselectDropdown =
                (configuration.type === EFieldTypes.apiDropdown || configuration.type === EFieldTypes.predefined) &&
                (configuration.operators as ESqlOperators[]).includes(ESqlOperators.$in);
              const isConfigurationForDateRange =
                configuration.type === EFieldTypes.date &&
                (configuration.operators as ESqlOperators[]).includes(ESqlOperators.$between);

              return {
                ...configuration,
                operators: identity.operators.filter(
                  (operator) =>
                    (configuration.operators as ESqlOperators[]).includes(operator) ||
                    (isEqAllowed &&
                      operator === ESqlOperators.$eq &&
                      (isConfigurationForMultiselectDropdown || isConfigurationForDateRange)),
                ),
              } as TAdvancedFilterConfig;
            })
            .filter(({ operators }) => operators.length),
        };
      });
  }

  public getFieldName(field: string, idOfCustom: number | undefined): string {
    return idOfCustom
      ? (this.manager.configuration().find((configuration) => configuration.idOfCustom === idOfCustom)?.field ?? field)
      : this.translate.instant(`advanceFilter.field.${field}`);
  }

  public getFieldId(field: string, idOfCustom: number | undefined): string {
    return idOfCustom
      ? (this.manager.configuration().find((configuration) => configuration.idOfCustom === idOfCustom)?.field ?? field)
      : field;
  }
}
